/**
 * FXC-9009 — RANS — V100 (local) solve → run-node persist payload assembly.
 *
 * A V100 RANS solve is computed on the local box (see `ransV100Client.ts`). To make
 * its point join the ADB grid / Tailor / lineage like a cloud-RANS or WebGPU run, the
 * surface POSTs the already-converged coefficients to the SAME `/highlift/webgpu-run`
 * endpoint the Phase-P6 WebGPU persist uses (`persistWebGpuRun`), which writes a `run`
 * node reusing the SAME rans_provenance flat axes + run_path scheme as the RANS `/solve`
 * path (see thread-api highlift.py). Because the design/operation records are flattened
 * IDENTICALLY, the V100 run dedupes onto / sits beside its WebGPU and cloud-RANS
 * counterparts on the same design axes.
 *
 * This module is the pure assembly of that run-node payload from the V100 solve result.
 * It deliberately reuses `webgpuDesignPayload` / `webgpuLineageSlug` (the proven P6 axis
 * flatten + buffer-lineage rule) so the V100 run flattens to the EXACT same axes — only
 * the provenance/solver tag differs (`RANS_V100_SOLVER_TAG = 'rans-v100'`), so V100 runs
 * are labeled/colored distinctly while sharing the design axes of their counterparts.
 *
 * NOTE on the provenance tag: the shared `/highlift/webgpu-run` route currently hardcodes
 * `metadata.solver = 'webgpu'` server-side and ignores extra body fields (the pydantic
 * model has no `extra="forbid"`). So a V100 run persisted through this path lands on the
 * grid on the correct axes (dedupe/lineage work), but its persisted `metadata.solver` is
 * 'webgpu' until a one-line server change threads a `solver` override through the route.
 * `RANS_V100_SOLVER_TAG` is the client-side intent (used for labeling and for the
 * one-line server passthrough when it lands). Extracted so it can be unit-tested without
 * rendering the heavy surface.
 */
import type { HighLiftAirfoil } from "./geometry";
import type { WebGpuRunRequest } from "../../api/highlift";
import type { RansV100Result } from "./ransV100Client";
import { webgpuDesignPayload, webgpuLineageSlug } from "./webgpu-solver/webgpuPersist";

/** The client-side provenance tag distinguishing V100 runs from WebGPU/cloud-RANS runs.
 *  (See the module doc: the shared route stamps 'webgpu' until a server passthrough lands.) */
export const RANS_V100_SOLVER_TAG = "rans-v100" as const;

// Re-export the shared axis-flatten + lineage rule so the V100 path flattens to the SAME
// design axes as the WebGPU path (and dedupes onto the same grid node). One source of truth.
export { webgpuDesignPayload as ransV100DesignPayload, webgpuLineageSlug as ransV100LineageSlug };

/** Assemble the `WebGpuRunRequest` to persist a successful V100 RANS solve. `flow` is the
 *  live freestream condition (mach/reynolds); `result` is the converged V100 solve's
 *  coefficients. The design/operation match `/solve` (and the WebGPU persist) so the run
 *  joins the grid identically and dedupes onto its WebGPU/cloud counterpart on the same axes. */
export function buildRansV100RunRequest(args: {
  config: HighLiftAirfoil;
  alphaDeg: number;
  flow: { mach: number; reynolds: number };
  result: Pick<RansV100Result, "cl" | "cd" | "lOverD" | "flow">;
  geometrySlug: string | null;
}): WebGpuRunRequest {
  const { config, alphaDeg, flow, result, geometrySlug } = args;
  return {
    alpha_deg: alphaDeg,
    mach: flow.mach,
    reynolds: flow.reynolds,
    CL: result.cl,
    // V100 reports a real (pressure) CD — coerce a non-finite value to null like the WebGPU path.
    CD: Number.isFinite(result.cd) ? result.cd : null,
    // V100 does not report a pitching moment in the workbench result; leave it null.
    CM: null,
    L_over_D: result.lOverD != null && Number.isFinite(result.lOverD) ? result.lOverD : null,
    design: webgpuDesignPayload(config),
    operation: { ...config.operation },
    geometry_slug: geometrySlug,
    // Threaded through the /webgpu-run route → persisted as metadata.solver='rans-v100'
    // (the route sanitizes to {"webgpu","rans-v100"}); labels V100 runs distinctly on the grid.
    solver: RANS_V100_SOLVER_TAG,
    // The real RANS slice — blob-stored server-side so it survives reload.
    flow_mesh: result.flow ?? null,
  };
}
